package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The mark: a bracket enclosing a rotating glyph.
 *
 * `docs/DESIGN.md` specifies exactly this, and the site shipped the static fallback — `[あ]`, one
 * script, forever — which quietly turned the spec's degradation case into the spec. The bracket is
 * the constant; what sits inside it is the product's whole subject rotating through.
 *
 * **The glyphs are one word.** Every character here is the first letter of "hello" in a script the
 * product actually handles, taken from the same vetted set as the glyph mosaic. It is not a random
 * parade of exotic characters — a localization tool using scripts as decoration would be exactly
 * the wrong joke.
 *
 * **Degradation is the point of the ordering.** `あ` is first, so a reader whose animation never
 * runs — a suspended tab, a prerender, reduced motion, no JavaScript at all — sees precisely the
 * mark the brand already ships. Nothing here can produce an empty bracket.
 */
private val WORDMARK_GLYPHS = listOf("あ", "한", "中", "Ω", "ب", "ह", "Я", "A")

/** Slow. This sits in a header a reader passes under constantly; anything quicker is a flicker. */
private const val ROTATE_INTERVAL_MS = 3_600L

/**
 * Fast enough to read as work in progress, on the one surface where that is the point.
 *
 * The handoff between marketing and the app has nothing else on it, so the mark is doing two jobs
 * there: it is still the mark, and it is the only evidence that something is happening. At the
 * header's 3.6s cadence a session that resolves in 800ms never advances a single glyph, and the
 * screen reads as frozen.
 */
const val WORDMARK_ROTATE_HANDOFF_MS = 520L

@Composable
fun LWordmarkGlyph(modifier: Modifier = Modifier, intervalMs: Long = ROTATE_INTERVAL_MS) {
    val reducedMotion = rememberReducedMotion()
    var index by remember { mutableStateOf(0) }

    LaunchedEffect(reducedMotion, intervalMs) {
        if (reducedMotion) {
            index = 0
            return@LaunchedEffect
        }
        while (true) {
            delay(intervalMs)
            index = (index + 1) % WORDMARK_GLYPHS.size
        }
    }

    Span(
        modifier.toAttrs {
            // The rotation is decoration; the wordmark beside it already says "Lingvo". Without
            // this, a screen reader announces a bracket and a character that changes under it.
            attr("aria-hidden", "true")
        },
    ) {
        Text("[")
        // Keyed so the glyph swaps cleanly rather than the browser trying to diff one character
        // into another mid-transition.
        androidx.compose.runtime.key(index) {
            Span(Modifier.toAttrs { attr("lang", GLYPH_LANGS[index]) }) {
                Text(WORDMARK_GLYPHS[index])
            }
        }
        Text("]")
    }
}

/** Tagged so each glyph is rendered with its own script's font, not a fallback that mangles it. */
private val GLYPH_LANGS = listOf("ja", "ko", "zh", "el", "ar", "hi", "ru", "en")
